import axios from 'axios';
import {BulkBody} from '@pluto/@types';
import {formQuery} from './form-query';

import {
    AudioWaveformParams,
    QcJobItemMedia,
    QcJobItemMediaAudioWaveform,
    TimespanCategory,
    TimespanCategoryType,
    TimespanNote,
    TimespanNoteTypeParams,
    TimespanNoteBody,
    TimespanParams,
    TimespanBody,
    QcJobItemMediaTimedText,
    TimespanMarker,
    TimespanMarkupBody,
    TimespanMarkupUpdateBody,
    TimespanAdBreak,
    Thumbnail,
    ThumbnailParams,
    CreationArtworkBody,
} from './qc-types';
import {
    CreateIssueParams,
    JiraIssue,
    CreateMultipleIssueParams,
} from '../../../QCWorkflow/QCJobs/QCJobsDialogs/JiraMenu/JiraTypes';

export const createSnapshot = async (frameNumber: number, videoFileId: number) => {
    const {data} = await axios.post(
    '/api/snapshots',
    {
            frameNumber,
            videoFileId,
        },
    );
    return data;
}

export const getSnapshots = async (videoFileId: number) => {
    const query = formQuery('/api/snapshots', {
        videoFileId
    });
    const {data} = await axios(query);

    return data;
}

export const getQcWorkQueue = async (params: any) => {
    const query = formQuery('/api/qc-work-queue', params);
    const {data} = await axios(query);

    return data;
};

export const getQCJobs = async (params: any) => {
    const query = formQuery('/api/qc-jobs', params);
    const {data} = await axios(query);

    return data;
};

export const downloadFile = (data: any) => {
    const blob = new Blob([data]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.setAttribute('download', 'qc-jobs.csv');
    document.body.append(link);
    link.click();
    link.remove();
};

export const getQCJobsExport = async (params: any) => {
    const url = formQuery('/api/qc-jobs/export', params);
    const {data} = await axios({
        url,
        method: 'GET',
        responseType: 'blob',
    });
    downloadFile(data);
};

export const changeQCJobPriority = async (body: any) => {
    return await axios.put('/api/qc-jobs/bulk', body);
};

export const setQcJobDueDate = async (body: any) => {
    return await axios.put(`/api/qc-jobs/bulk`, body);
};

export const assignQCToUser = async (body: any) => {
    return await axios.put(`/api/qc-jobs/bulk`, body);
};

export const unassignQCFromUser = async (body: any) => {
    return await axios.put(`/api/qc-jobs/bulk`, body);
};

export const setJobStatus = async (body: BulkBody[]) => {
    return await axios.put(`/api/qc-jobs/bulk`, body);
};

export const getQCJobPublishingRights = async (id: any) => {
    const {data} = await axios(`/api/qc-jobs/${id}/rights`);
    return data;
};

export const getContentItemViewPublishingRights = async (id: any) => {
    const {data} = await axios(`/api/content-items/${id}/rights`);
    return data;
};

export const getQCJobsSummary = async () => {
    const {data} = await axios('/api/qc-jobs/summary');
    return data;
};

export const getQCWorkQueueSummary = async () => {
    const {data} = await axios('/api/qc-work-queue/summary');
    return data;
};

export const getDeliverySummary = async () => {
    const {data} = await axios('/api/delivery-jobs/summary');
    return data;
};

export const getQCJobMedia = async (id: string | number): Promise<QcJobItemMedia> => {
    const {data} = await axios(`/api/qc-jobs/${id}/media`);
    return data;
};

export const getContentItemViewMedia = async (
    id: string | number
): Promise<QcJobItemMedia> => {
    const {data} = await axios(`/api/content-items/${id}/media`);
    return data;
};

export const getQCJobMediaAudioWaveform = async (
    url: string,
    params: AudioWaveformParams
): Promise<QcJobItemMediaAudioWaveform> => {
    const query = formQuery(url, params);
    const {data} = await axios(query);
    return data;
};

export const getTimespanCategories = async (
    type: TimespanCategoryType
): Promise<TimespanCategory[]> => {
    const {data} = await axios(`/api/timespan-categories/${type}`);
    return data;
};

export const getQCJobTimespanAdBreaks = async (
    url: string,
    params: TimespanParams
): Promise<TimespanAdBreak[]> => {
    const query = formQuery(url, params);
    const {data} = await axios(query);
    return data;
};

export const createQCJobTimespanAdBreak = async (
    url: string,
    body: TimespanBody
): Promise<TimespanAdBreak> => {
    const {data} = await axios.post(url, body);
    return data;
};

export const updateQCJobTimespanAdBreak = async (
    url: string,
    body: TimespanBody
): Promise<TimespanAdBreak> => {
    const {data} = await axios.put(url, body);
    return data;
};

export const deleteSnapshot = async (id: number) => {
    const deleteUrl = `/api/snapshots/${id}`;

    return await axios.delete(deleteUrl);
};

export const deleteQCJobTimespanAdBreak = async (url: string): Promise<void> => {
    return await axios.delete(url);
};

export const getQCJobTimespanNotes = async (
    url: string,
    params: TimespanNoteTypeParams
): Promise<TimespanNote[]> => {
    const query = formQuery(url, params);
    const {data} = await axios(query);
    return data;
};

export const createArtworkOfSnapshot = async (
    id: number,
    params: CreationArtworkBody,
) => {
    const url = `/api/snapshots/${id}/artwork`;
    const {data} = await axios.post(url, params);

    return data;
}

export const createQCJobTimespanNote = async (
    url: string,
    body: TimespanNoteBody
): Promise<TimespanNote> => {
    const {data} = await axios.post(url, body);
    return data;
};

export const updateQCJobTimespanNote = async (
    url: string,
    body: TimespanNoteBody
): Promise<TimespanNote> => {
    const {data} = await axios.put(url, body);
    return data;
};

export const deleteQCJobTimespanNote = async (url: string) => {
    return await axios.delete(url);
};

export const createQCJobTimespanMarkup = async (
    url: string,
    body: TimespanMarkupBody
): Promise<TimespanMarker> => {
    const {data} = await axios.post(url, body);
    return data;
};

export const updateQCJobTimespanMarkup = async (
    url: string,
    body: TimespanMarkupUpdateBody
): Promise<TimespanMarker> => {
    const {data} = await axios.put(url, body);
    return data;
};

export const deleteQCJobTimespanMarkup = async (url: string) => {
    return await axios.delete(url);
};

export const getQCJobTimespanMarkups = async (
    url: string,
    params: TimespanParams
): Promise<TimespanMarker[]> => {
    const query = formQuery(url, params);
    const {data} = await axios(query);
    return data;
};

export const getQCJobTimespanBlackFrames = async (
    url: string,
    params: TimespanParams
): Promise<TimespanMarker[]> => {
    const query = formQuery(url, params);
    const {data} = await axios(query);
    return data;
};

export const getQCJobMediaTimedText = async (
    url: string
): Promise<QcJobItemMediaTimedText[]> => {
    const {data} = await axios(url);
    return data;
};

export const createJiraIssue = async (params: CreateIssueParams): Promise<JiraIssue> => {
    const {id, body} = params;
    const {data} = await axios.post(`/api/qc-jobs/${id}/issues`, body);

    return data;
};

export const createJiraMultipleIssue = async (
    params: CreateMultipleIssueParams
): Promise<JiraIssue> => {
    const {body} = params;
    const {data} = await axios.post(`/api/qc-jobs/issues`, body);

    return data;
};

export const getQCJobThumbnails = async (
    url: string,
    params: ThumbnailParams
): Promise<Thumbnail[]> => {
    const query = formQuery(url, params);
    const {data} = await axios(query);
    return data;
};
